import {observer} from 'mobx-react-lite'
import React, {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {DeadlineStickerValue, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {LinkButtonSmall} from '@components/common/button/button'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {useBoolean} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {DeadlineEdit} from '@extensions/sticker/views-web/deadline/deadline-edit'
import {
  toServerDeadline,
  toVisualDeadline
} from '@extensions/sticker/views-web/deadline/deadline-sticker-util'
import {useMultiActionsWithTasksStore} from '../multi-actions-with-tasks-store'

const DROPDOWN_OFFSET: [number, number] = [-120, 4]

export const DeadlineSticker = observer(
  React.forwardRef(
    (
      {
        task,
        cb
      }: {
        task: Hub
        cb: () => void
      },
      ref: React.MutableRefObject<HTMLElement>
    ) => {
      const [state, store] = useMultiActionsWithTasksStore()
      if (!state.tag) {
        return null
      }
      const open = useBoolean(true)
      useEffect(() => {
        !open.value && cb()
      }, [open.value])
      useEffect(() => {
        open.turnOn()
      }, [state.tag])

      const close = (discardChanges?: boolean) => {
        if (discardChanges) {
          return
        }
        if (state.deadlineValue) {
          const newVal = toServerDeadline(task, state.deadlineValue)
          const box = Sync.getCurrentSandbox()
          for (const id of Array.from(state.tasksIds)) {
            const task = box.getHub(id)
            if (!task || !state.tag) {
              continue
            }
            Sticker.pin(state.tag, task.location, task, newVal)
          }
        }
        open.turnOff()
        cb()
      }
      const setValue = (value: DeadlineStickerValue) => {
        store.setDeadlineValue(value)
      }
      const val = toVisualDeadline(task, task.stickers[state.tag.id])

      const unpin = () => {
        const box = Sync.getCurrentSandbox()
        for (const id of Array.from(state.tasksIds)) {
          const task = box.getHub(id)
          if (!task || !state.tag) {
            continue
          }
          Sticker.unpin(state.tag, task)
        }
        close(true)
        state.tag && Sticker.unpin(state.tag, task)
      }

      return (
        <Dropdown
          visible={open.value}
          placement="top-start"
          offset={DROPDOWN_OFFSET}
          onVisibleChange={close}
          content={
            <DropdownContent
              className="z-10 min-w-248"
              header={
                <>
                  <div>{Lang.msg.stickerNames['deadline']}</div>
                  <LinkButtonSmall onClick={unpin}>
                    {Lang.msg.unpin}
                  </LinkButtonSmall>
                </>
              }
            >
              <DeadlineEdit val={val} setCurrentValue={setValue} task={task} />
            </DropdownContent>
          }
          reference={ref}
          hideArrow
        />
      )
    }
  )
)
